import { useState, useEffect, useRef } from "react";
import "./OtpLoginPage.scss";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import toast from "react-hot-toast";
import {
  useRequestOtpMutation,
  useResendOtpMutation,
  useVerifyOtpMutation,
} from "../../features/api/authApiSlice";
import { setCredentials } from "../../features/auth/authSlice";
import { useRecaptcha, executeRecaptcha } from "../../hooks/useRecaptcha";

const SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY;

const RESEND_DELAY = 60; // seconds

const OtpLoginPage = () => {
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [step, setStep] = useState(1);
  const [countdown, setCountdown] = useState(0);
  const [expiresAt, setExpiresAt] = useState(null);
  const [formDisabled, setFormDisabled] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const inputRefs = useRef([]);
  const [requestOtp, { isLoading: requesting }] = useRequestOtpMutation();
  const [verifyOtp, { isLoading: verifying }] = useVerifyOtpMutation();
  const [resendOtp, { isLoading: resending }] = useResendOtpMutation();

  useRecaptcha(SITE_KEY, ["/otplogin"]); // ✅ only show badge on /login

  useEffect(() => {
    let timer;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown((c) => c - 1), 1000);
    } else if (countdown === 0 && step === 2) {
      setFormDisabled(true);
      handleAutoResend();
    }
    return () => clearTimeout(timer);
  }, [countdown]);

  // Auto-submit on full OTP
  useEffect(() => {
    const joined = otp.join("");
    if (joined.length === 6 && !formDisabled) {
      handleVerify(new Event("submit"));
    }
  }, [otp]);

  const handleAutoResend = async () => {
    if (email) {
      try {
        const token = await executeRecaptcha(SITE_KEY, "otplogin");
        await resendOtp({ email, recaptchaToken: token }).unwrap();
        handleOtpSent();
      } catch (err) {
        console.log("Auto resend failed:", err);
      }
    }
  };

  // Paste support (paste a full 6-digit code into any field)
  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData("text").slice(0, 6).split("");
    if (pasted.length === 6) {
      setOtp(pasted);
      inputRefs.current[5]?.focus();
    }
  };

  const formatTime = (date) =>
    date.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });

  const handleOtpSent = () => {
    const now = new Date();
    const expiration = new Date(now.getTime() + RESEND_DELAY * 1000);
    setExpiresAt(expiration);
    setCountdown(RESEND_DELAY);
    setFormDisabled(false);
    setOtp(["", "", "", "", "", ""]);
    inputRefs.current[0]?.focus();
  };

  const handleRequest = async (e) => {
    e.preventDefault();
    try {
      const token = await executeRecaptcha(SITE_KEY, "otplogin");
      const res = await requestOtp({ email, recaptchaToken: token }).unwrap();
      toast.success(
        res?.message || "Please check your email for the OTP Code."
      );
      setStep(2);
      handleOtpSent();
    } catch (error) {
      toast.error(error?.data?.message || "Email is required!");
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    const joinedOtp = otp.join("");

    const token = await executeRecaptcha(SITE_KEY, "otplogin");

    try {
      const res = await verifyOtp({
        email,
        otp: joinedOtp,
        recaptchaToken: token,
      }).unwrap();

      dispatch(
        setCredentials({ accessToken: res.accessToken, user: res.user })
      );
      navigate("/");
      toast.success(res?.message || "OTP Verification successfully!");
    } catch (err) {
      const status = err?.status;
      if (status === 401 || status === 403) {
        toast.error(err?.data?.message || "OTP Verification failed..");
      } else if (status === 500) {
        toast.error("Server error. Please try again later.");
      } else {
        toast.error(err?.data?.message || "Verification failed.");
      }
    }
  };

  const handleResend = async () => {
    try {
      const token = await executeRecaptcha(SITE_KEY, "otplogin");
      const res = await resendOtp({ email, recaptchaToken: token }).unwrap();
      toast.success(
        res?.message || "OTP send successfully, please check your email."
      );
      handleOtpSent();
    } catch (error) {
      toast.error(
        error?.data?.message || "Failed to resend OTP. Please try again."
      );
    }
  };

  const handleChange = (index, value) => {
    const updated = [...otp];
    updated[index] = value.slice(-1);
    setOtp(updated);
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  return (
    <div className="auth-form">
      <div className="main">
        {step === 1 ? (
          <div className="auth-from-container">
            <h2>OTP Login</h2>
            <form onSubmit={handleRequest} className="otp-form">
              <input
                type="email"
                placeholder="Enter email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <button type="submit" disabled={requesting}>
                {requesting ? "Sending OTP..." : "Send OTP"}
              </button>
            </form>
          </div>
        ) : (
          <div className="auth-from-container">
            <h2>OTP Login</h2>
            <form onSubmit={handleVerify} className="otp-form">
              <div className="otp-input-group">
                {otp?.map((val, i) => (
                  <input
                    pattern="\d"
                    required
                    key={i}
                    type="text"
                    maxLength="1"
                    value={val}
                    onChange={(e) => handleChange(i, e.target.value)}
                    ref={(el) => (inputRefs.current[i] = el)}
                    disabled={formDisabled}
                    onPaste={handlePaste}
                  />
                ))}
              </div>
              <button type="submit" disabled={verifying || formDisabled}>
                {verifying ? "Verifying..." : "Verify OTP"}
              </button>

              <button
                type="button"
                onClick={handleResend}
                disabled={resending || countdown > 0}
              >
                {resending
                  ? "Resending..."
                  : countdown > 0
                  ? `Resend in ${countdown}s`
                  : "Resend OTP"}
              </button>

              {expiresAt && (
                <p className="otp-expiration">
                  OTP expires at: <strong>{formatTime(expiresAt)}</strong>
                </p>
              )}
              {formDisabled && (
                <p className="otp-expiration error">
                  OTP expired. A new one will be sent shortly.
                </p>
              )}
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default OtpLoginPage;
